import './FilterMenu.scss';
import React from 'react';
import { Dropdown, Form, ToggleButton } from 'react-bootstrap';
import { MdCircle, MdFilterList } from 'react-icons/md';
import { EventEntryMethods, EventStatuses, EventType, EventTypes } from '../../Constants/eventtype';
import classNames from 'classnames';
import FilterDropdown from './FilterDropdown/FilterDropdown';

export type Filter = {
  types: string[];
  statuses: string[];
  entryMethods: string[];
};

export interface FilterMenuProps {
  selectedZip: string;
  selectedRadius: string;
  selectedFilters: Filter;
  onUpdateZip: (zip: string) => void;
  onUpdateRadius: (radius: string) => void;
  onUpdateFilter: (filter: 'type' | 'status' | 'entryMethod', target: string) => void;
}

const FilterMenu: React.FC<FilterMenuProps> = ({
  selectedZip,
  selectedRadius,
  selectedFilters,
  onUpdateZip,
  onUpdateRadius,
  onUpdateFilter,
}) => {
  const { types: eventTypeFilters, statuses: statusFilters, entryMethods: entryMethodFilters } = selectedFilters;

  const locationFilterContent = (
    <>
      <Form.Group className="mb-4">
        <Form.Label className="text-uppercase m-0 fw-bold small">Zip Code</Form.Label>
        <Form.Control
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={5}
          value={selectedZip}
          onChange={e => onUpdateZip(e.target.value)}
        />
      </Form.Group>
      <Form.Group>
        <Form.Label className="d-flex justify-content-between mb-2 fw-bold small">
          <p className="text-uppercase m-0">Radius</p>
          <p className="m-0">{selectedRadius} mi</p>
        </Form.Label>
        <Form.Range value={selectedRadius} onChange={e => onUpdateRadius(e.target.value)} min={1} max={200} />
      </Form.Group>
    </>
  );

  const eventTypeFilterContent = (
    <Form.Group>
      <Form.Label className="text-uppercase fw-bold small mb-2">Event Type</Form.Label>
      <div className="d-flex align-items-start gap-1 flex-wrap">
        <ToggleButton
          key="Type_ALL"
          type="checkbox"
          className="px-3 py-1 rounded-pill small text-body"
          variant="outline-light"
          checked={eventTypeFilters.length === 0}
          value={'ALL'}
          onClick={e => {
            e.preventDefault();
            onUpdateFilter('type', 'ALL');
          }}>
          <span className="small">All</span>
        </ToggleButton>
        {Object.keys(EventTypes).map(type => (
          <ToggleButton
            key={type}
            type="checkbox"
            className={classNames(
              `px-3 py-1 rounded-pill small text-nowrap text-${EventTypes[type].color}`,
              !eventTypeFilters.includes(type) && `border-${EventTypes[type].color}`
            )}
            variant={`outline-${EventTypes[type].color}-light`}
            checked={eventTypeFilters.includes(type)}
            value={type}
            onClick={e => {
              e.preventDefault();
              onUpdateFilter('type', type);
            }}>
            <span className="small">{EventTypes[type].name}</span>
          </ToggleButton>
        ))}
      </div>
    </Form.Group>
  );

  const statusFilterContent = (
    <Form.Group>
      <Form.Label className="text-uppercase fw-bold small mb-2">Status</Form.Label>
      <div className="d-flex align-items-start gap-1 flex-wrap">
        <ToggleButton
          key="Status_ALL"
          type="checkbox"
          className="px-3 py-1 rounded-pill small text-body"
          variant="outline-light"
          checked={statusFilters.length === 0}
          value={'ALL'}
          onClick={e => {
            e.preventDefault();
            onUpdateFilter('status', 'ALL');
          }}>
          <span className="small">All</span>
        </ToggleButton>
        {Object.keys(EventStatuses).map(status => (
          <ToggleButton
            key={status}
            type="checkbox"
            className="px-3 py-1 rounded-pill small text-body text-nowrap"
            variant="outline-light"
            checked={statusFilters.includes(status)}
            value={status}
            onClick={e => {
              e.preventDefault();
              onUpdateFilter('status', status);
            }}>
            <span className="small">{EventStatuses[status]}</span>
          </ToggleButton>
        ))}
      </div>
    </Form.Group>
  );

  const entryMethodFilterContent = (
    <Form.Group>
      <Form.Label className="text-uppercase fw-bold small mb-2">Entry Method</Form.Label>
      <div className="d-flex align-items-start gap-1 flex-wrap">
        <ToggleButton
          key="Method_ALL"
          type="checkbox"
          className="px-3 py-1 rounded-pill small text-body"
          variant="outline-light"
          checked={entryMethodFilters.length === 0}
          value={'ALL'}
          onClick={e => {
            e.preventDefault();
            onUpdateFilter('entryMethod', 'ALL');
          }}>
          <span className="small">All</span>
        </ToggleButton>
        {Object.keys(EventEntryMethods).map(method => (
          <ToggleButton
            key={method}
            type="checkbox"
            className="px-3 py-1 rounded-pill small text-body text-nowrap"
            variant="outline-light"
            checked={entryMethodFilters.includes(method)}
            value={method}
            onClick={e => {
              e.preventDefault();
              onUpdateFilter('entryMethod', method);
            }}>
            <span className="small">{EventEntryMethods[method]}</span>
          </ToggleButton>
        ))}
      </div>
    </Form.Group>
  );

  const largeFilterMenu = (
    <div className="w-100 d-flex align-items-center rounded-pill border">
      <FilterDropdown label="Location" className="location">
        <FilterDropdown.Toggle>
          <span className="d-flex align-items-center gap-2 text-truncate">
            {selectedZip}
            <span className="opacity-25">|</span>
            {selectedRadius} mi
          </span>
        </FilterDropdown.Toggle>
        <FilterDropdown.Menu>{locationFilterContent}</FilterDropdown.Menu>
      </FilterDropdown>

      <div className="divider h-50 border-start" />

      <FilterDropdown label="Events" className="events">
        <FilterDropdown.Toggle className={classNames(eventTypeFilters.length === 0 && 'opacity-50')}>
          {eventTypeFilters.length > 0
            ? eventTypeFilters.map(type => EventTypes[type].name).join(', ')
            : 'Select event types'}
        </FilterDropdown.Toggle>
        <FilterDropdown.Menu>{eventTypeFilterContent}</FilterDropdown.Menu>
      </FilterDropdown>

      <div className="divider h-50 border-start" />

      <FilterDropdown label="Status" className="status" align="end">
        <FilterDropdown.Toggle className={classNames(statusFilters.length === 0 && 'opacity-50')}>
          {statusFilters.length > 0
            ? statusFilters.map(status => EventStatuses[status]).join(', ')
            : 'Select registration statuses'}
        </FilterDropdown.Toggle>
        <FilterDropdown.Menu>{statusFilterContent}</FilterDropdown.Menu>
      </FilterDropdown>

      <div className="divider h-50 border-start" />

      <FilterDropdown label="Entry Method" className="method" align="end">
        <FilterDropdown.Toggle className={classNames(entryMethodFilters.length === 0 && 'opacity-50')}>
          {entryMethodFilters.length > 0
            ? entryMethodFilters.map(method => EventEntryMethods[method]).join(', ')
            : 'Select entry methods'}
        </FilterDropdown.Toggle>
        <FilterDropdown.Menu>{entryMethodFilterContent}</FilterDropdown.Menu>
      </FilterDropdown>
    </div>
  );

  const smallFilterMenu = (
    <Dropdown align="start">
      <Dropdown.Toggle variant="outline-light" className="filter-dropdown text-body rounded-3">
        <span className="d-flex align-items-center gap-2">
          Filter <MdFilterList />
        </span>
        {(selectedRadius !== '100' ||
          statusFilters.length > 0 ||
          eventTypeFilters.length > 0 ||
          entryMethodFilters.length > 0) && (
          <MdCircle
            size={14}
            className="text-blue filter-indicator position-absolute top-0 start-100 translate-middle"
          />
        )}
      </Dropdown.Toggle>
      <Dropdown.Menu className="filter-dropdown-menu border-0 p-3 shadow">
        <div className="d-flex gap-3 flex-column">
          {locationFilterContent}
          {eventTypeFilterContent}
          {statusFilterContent}
          {entryMethodFilterContent}
        </div>
      </Dropdown.Menu>
    </Dropdown>
  );

  return (
    <>
      <div className="filter-menu lg d-none d-md-flex flex-fill">{largeFilterMenu}</div>
      <div className="filter-menu sm d-flex d-md-none">{smallFilterMenu}</div>
    </>
  );
};

export default FilterMenu;
